// client/src/pages/AdminPage.jsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Building2, ClipboardList, Flag, Search, Plus, X, ChevronDown, UserCheck, Mail, Link } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import {
  useAdminUsers, useAdminUpdateUser, useAdminCreateUser, useAdminCustomerLookup,
  useAdminGarages, useAdminCreateGarage,
  useAuditLogs,
  useFeatureFlags, useSetFeatureFlag, useDeleteFeatureFlag,
  useAdminTestEmail,
} from '../api/hooks'
import { useAuth } from '../context/AuthContext'
import EmailLogPage from './EmailLogPage'

// ─── Shared helpers ───────────────────────────────────────────────────────────

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

const ROLES = ['customer', 'garage_user', 'garage_admin', 'platform_admin']

const ROLE_BADGE = {
  customer:       'bg-gray-500/15 text-gray-400',
  garage_user:    'bg-blue-500/15 text-blue-400',
  garage_admin:   'bg-indigo-500/15 text-indigo-400',
  platform_admin: 'bg-purple-500/15 text-purple-400',
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric',
}) : '—'

const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
}) : '—'

function RoleBadge({ role }) {
  return (
    <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full capitalize whitespace-nowrap',
      ROLE_BADGE[role] || 'bg-gray-500/15 text-gray-400')}>
      {role?.replace(/_/g, ' ')}
    </span>
  )
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'users',    label: 'Users',         icon: Users },
  { id: 'garages',  label: 'Garages',       icon: Building2 },
  { id: 'audit',    label: 'Audit Logs',    icon: ClipboardList },
  { id: 'flags',    label: 'Feature Flags', icon: Flag },
  { id: 'emails',   label: 'Emails',        icon: Mail },
]

// ─── Create User modal ────────────────────────────────────────────────────────

const GARAGE_ROLES = ['garage_user', 'garage_admin']

function CreateUserModal({ onClose }) {
  const create             = useAdminCreateUser()
  const { data: garages = [] } = useAdminGarages()
  const [form, setForm]    = useState({ name: '', email: '', role: 'customer', garageId: '' })
  const [debouncedEmail, setDebouncedEmail] = useState('')
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  // Debounce email for lookup — only fires after user stops typing
  useEffect(() => {
    const t = setTimeout(() => setDebouncedEmail(form.email.trim()), 500)
    return () => clearTimeout(t)
  }, [form.email])

  const { data: customerMatches = [] } = useAdminCustomerLookup(
    form.role === 'customer' ? debouncedEmail : ''
  )

  const needsGarage = GARAGE_ROLES.includes(form.role)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim())  return toast.error('Name is required')
    if (!form.email.trim()) return toast.error('Email is required')
    if (needsGarage && !form.garageId) return toast.error('Please select a garage')
    try {
      const result = await create.mutateAsync({
        name:     form.name.trim(),
        email:    form.email.trim(),
        role:     form.role,
        garageId: needsGarage ? form.garageId : undefined,
      })
      const linked = result.linkedCustomers ?? []
      if (linked.length) {
        const totalVehicles = linked.reduce((s, c) => s + (c.vehicleCount || 0), 0)
        toast.success(
          `User created and linked to ${linked.length} customer record${linked.length > 1 ? 's' : ''}` +
          (totalVehicles ? ` — ${totalVehicles} vehicle${totalVehicles > 1 ? 's' : ''} connected` : '')
        )
      } else {
        toast.success('User created — welcome email sent')
      }
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create user')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Add User</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Name *</label>
            <input className={inputCls} value={form.name} onChange={set('name')} required placeholder="Full name" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Email *</label>
            <input className={inputCls} type="email" value={form.email} onChange={set('email')} required placeholder="email@example.com" />
          </div>

          {/* Customer match banner — shown when role is customer and email matches an unlinked record */}
          {form.role === 'customer' && customerMatches.length > 0 && (
            <div className="flex items-start gap-3 px-3 py-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
              <Link size={15} className="text-indigo-400 mt-0.5 shrink-0" />
              <div className="text-xs text-indigo-300 space-y-0.5">
                {customerMatches.map(c => (
                  <p key={c.id}>
                    <span className="font-semibold text-indigo-200">{c.name}</span>
                    {c.garage_name ? ` · ${c.garage_name}` : ''}
                    {c.vehicle_count > 0
                      ? ` · ${c.vehicle_count} vehicle${c.vehicle_count > 1 ? 's' : ''}`
                      : ''}
                  </p>
                ))}
                <p className="text-indigo-400 mt-1">
                  {customerMatches.length === 1
                    ? 'This customer record will be linked automatically on creation.'
                    : 'These customer records will be linked automatically on creation.'}
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Role *</label>
            <select className={clsx(inputCls, 'cursor-pointer')} value={form.role}
              onChange={e => setForm(p => ({ ...p, role: e.target.value, garageId: '' }))}>
              {ROLES.map(r => (
                <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          {needsGarage && (
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Garage *</label>
              <select className={clsx(inputCls, 'cursor-pointer')} value={form.garageId} onChange={set('garageId')} required>
                <option value="">Select garage…</option>
                {garages.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
          )}
          <p className="text-xs text-[var(--text-muted)] pt-1">
            A temporary password will be generated and emailed to the user.
          </p>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={create.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {create.isPending ? 'Creating…' : 'Add User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Create Garage modal ──────────────────────────────────────────────────────

function CreateGarageModal({ onClose }) {
  const create = useAdminCreateGarage()
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '' })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) return toast.error('Name is required')
    try {
      await create.mutateAsync(form)
      toast.success('Garage created')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create garage')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">New Garage</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {[
            { f: 'name',    label: 'Name',    required: true },
            { f: 'email',   label: 'Email' },
            { f: 'phone',   label: 'Phone' },
            { f: 'address', label: 'Address' },
          ].map(({ f, label, required }) => (
            <div key={f}>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                {label}{required && ' *'}
              </label>
              <input className={inputCls} value={form[f]} onChange={set(f)} required={required} />
            </div>
          ))}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={create.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {create.isPending ? 'Creating…' : 'Create Garage'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Users tab ────────────────────────────────────────────────────────────────

function UsersTab() {
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [pendingRole, setPendingRole] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const updateUser = useAdminUpdateUser()

  const { user: authUser, impersonateUser, viewMode } = useAuth()
  const navigate    = useNavigate()
  const queryClient = useQueryClient()

  const filters = {}
  if (search.trim()) filters.search = search.trim()
  if (roleFilter) filters.role = roleFilter

  const { data, isLoading } = useAdminUsers(filters)
  const users = data?.users || []
  const total = data?.total || 0

  const startEdit = (user) => {
    setEditingId(user.id)
    setPendingRole(user.role)
  }

  const saveRole = async (user) => {
    if (pendingRole === user.role) return setEditingId(null)
    if (!window.confirm(`Change ${user.name}'s role to "${pendingRole.replace(/_/g, ' ')}"?`)) return
    try {
      await updateUser.mutateAsync({ id: user.id, role: pendingRole })
      toast.success('Role updated')
      setEditingId(null)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update role')
    }
  }

  const toggleActive = async (user) => {
    const action = user.is_active ? 'suspend' : 'reactivate'
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${user.name}?`)) return
    try {
      await updateUser.mutateAsync({ id: user.id, isActive: !user.is_active })
      toast.success(`User ${action}d`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed')
    }
  }

  const handleImpersonate = async (user) => {
    if (!window.confirm(`Impersonate ${user.name}? You will have a 1-hour session as this user.`)) return
    try {
      await impersonateUser(user.id)
      queryClient.clear()
      navigate('/')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to impersonate user')
    }
  }

  const canImpersonate = authUser?.role === 'platform_admin' && viewMode === 'default'

  return (
    <div className="space-y-4">
      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} />}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            className={clsx(inputCls, 'pl-9')}
            placeholder="Search name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
          className={clsx(inputCls, 'w-auto cursor-pointer')}>
          <option value="">All roles</option>
          {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
        </select>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
                     text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
        >
          <Plus size={15} />
          Add User
        </button>
      </div>

      {!isLoading && (
        <p className="text-xs text-[var(--text-muted)]">{total} {total === 1 ? 'user' : 'users'}</p>
      )}

      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3 animate-pulse">
            {[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-[var(--bg-overlay)] rounded-lg" />)}
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--text-muted)]">No users found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-[var(--text-muted)] text-xs">
                  <th className="text-left px-4 py-3 font-medium">Name</th>
                  <th className="text-left px-4 py-3 font-medium">Email</th>
                  <th className="text-left px-4 py-3 font-medium">Role</th>
                  <th className="text-left px-4 py-3 font-medium">Garage</th>
                  <th className="text-left px-4 py-3 font-medium">Joined</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {users.map(user => (
                  <tr key={user.id} className={clsx('hover:bg-[var(--bg-overlay)]', !user.is_active && 'opacity-50')}>
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)] whitespace-nowrap">
                      {user.name}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">{user.email}</td>
                    <td className="px-4 py-3">
                      {editingId === user.id ? (
                        <div className="flex items-center gap-2">
                          <select value={pendingRole} onChange={e => setPendingRole(e.target.value)}
                            className="text-xs bg-[var(--bg-base)] border border-[var(--border)] rounded px-1.5 py-1 text-[var(--text-primary)] cursor-pointer focus:outline-none focus:border-indigo-500">
                            {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                          </select>
                          <button onClick={() => saveRole(user)}
                            className="text-xs text-indigo-400 hover:text-indigo-300 font-medium">Save</button>
                          <button onClick={() => setEditingId(null)}
                            className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)]">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => startEdit(user)}
                          className="flex items-center gap-1 group">
                          <RoleBadge role={user.role} />
                          <ChevronDown size={11} className="text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]" />
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] text-xs whitespace-nowrap">
                      {user.garage_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-muted)] text-xs whitespace-nowrap">
                      {fmtDate(user.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={clsx('px-2 py-0.5 text-xs rounded-full font-medium',
                        user.is_active ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400')}>
                        {user.is_active ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button onClick={() => toggleActive(user)}
                          disabled={updateUser.isPending}
                          className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] disabled:opacity-50 whitespace-nowrap">
                          {user.is_active ? 'Suspend' : 'Reactivate'}
                        </button>
                        {canImpersonate && user.id !== authUser?.id && (
                          <button
                            onClick={() => handleImpersonate(user)}
                            className="flex items-center gap-1 text-xs text-[var(--text-muted)]
                                       hover:text-indigo-400 transition-colors whitespace-nowrap"
                            title={`View as ${user.name}`}
                          >
                            <UserCheck size={12} />
                            View as
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Garages tab ──────────────────────────────────────────────────────────────

function GaragesTab() {
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const filters = search.trim() ? { search: search.trim() } : {}
  const { data: garages = [], isLoading } = useAdminGarages(filters)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            className={clsx(inputCls, 'pl-9')}
            placeholder="Search garages…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
                     text-white text-sm font-medium rounded-lg transition-colors">
          <Plus size={15} />
          New Garage
        </button>
      </div>

      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3 animate-pulse">
            {[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-[var(--bg-overlay)] rounded-lg" />)}
          </div>
        ) : garages.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--text-muted)]">No garages found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-[var(--text-muted)] text-xs">
                  <th className="text-left px-4 py-3 font-medium">Name</th>
                  <th className="text-left px-4 py-3 font-medium">Email</th>
                  <th className="text-left px-4 py-3 font-medium">Phone</th>
                  <th className="text-left px-4 py-3 font-medium">Staff</th>
                  <th className="text-left px-4 py-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {garages.map(g => (
                  <tr key={g.id} className="hover:bg-[var(--bg-overlay)]">
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)] whitespace-nowrap">{g.name}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{g.email || '—'}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{g.phone || '—'}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{g.staff_count}</td>
                    <td className="px-4 py-3 text-[var(--text-muted)] text-xs whitespace-nowrap">{fmtDate(g.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && <CreateGarageModal onClose={() => setShowCreate(false)} />}
    </div>
  )
}

// ─── Audit log tab ────────────────────────────────────────────────────────────

const AUDIT_ACTIONS = [
  'quote.approved', 'quote.rejected',
  'invoice.created', 'invoice.finalised',
  'service_record.finalised', 'service_record.admin_corrected',
  'job.status_changed', 'user.role_changed',
  'user.impersonated',
  'garage.created', 'vehicle.deleted',
]

const ENTITY_TYPES = ['quote', 'invoice', 'service_record', 'job', 'user', 'garage', 'vehicle']

function AuditTab() {
  const [action, setAction]         = useState('')
  const [entityType, setEntityType] = useState('')
  const filters = {}
  if (action) filters.action = action
  if (entityType) filters.entityType = entityType

  const { data, isLoading } = useAuditLogs(filters)
  const logs  = data?.logs  || []
  const total = data?.total || 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <select value={action} onChange={e => setAction(e.target.value)}
          className={clsx(inputCls, 'w-auto cursor-pointer')}>
          <option value="">All actions</option>
          {AUDIT_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={entityType} onChange={e => setEntityType(e.target.value)}
          className={clsx(inputCls, 'w-auto cursor-pointer')}>
          <option value="">All entities</option>
          {ENTITY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      {!isLoading && (
        <p className="text-xs text-[var(--text-muted)]">{total} {total === 1 ? 'entry' : 'entries'}</p>
      )}

      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3 animate-pulse">
            {[...Array(6)].map((_, i) => <div key={i} className="h-10 bg-[var(--bg-overlay)] rounded-lg" />)}
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--text-muted)]">No audit entries.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-[var(--text-muted)] text-xs">
                  <th className="text-left px-4 py-3 font-medium">When</th>
                  <th className="text-left px-4 py-3 font-medium">Actor</th>
                  <th className="text-left px-4 py-3 font-medium">Action</th>
                  <th className="text-left px-4 py-3 font-medium">Entity</th>
                  <th className="text-left px-4 py-3 font-medium">Changes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-[var(--bg-overlay)] align-top">
                    <td className="px-4 py-3 text-[var(--text-muted)] text-xs whitespace-nowrap">
                      {fmtDateTime(log.created_at)}
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      <p className="text-[var(--text-primary)]">{log.actor_name || 'System'}</p>
                      {log.actor_email && (
                        <p className="text-[var(--text-muted)]">{log.actor_email}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 text-xs font-mono bg-[var(--bg-base)] border border-[var(--border)] rounded text-[var(--text-secondary)] whitespace-nowrap">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--text-muted)] whitespace-nowrap">
                      <p className="capitalize">{log.entity_type?.replace(/_/g, ' ')}</p>
                      {log.entity_id && (
                        <p className="font-mono text-[10px]">{log.entity_id.slice(0, 8)}…</p>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {(log.old_values || log.new_values) && (
                        <div className="text-xs text-[var(--text-muted)] space-y-1">
                          {log.old_values && (
                            <p className="truncate">
                              <span className="text-red-400">−</span>{' '}
                              {JSON.stringify(log.old_values)}
                            </p>
                          )}
                          {log.new_values && (
                            <p className="truncate">
                              <span className="text-green-400">+</span>{' '}
                              {JSON.stringify(log.new_values)}
                            </p>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Feature flags tab ────────────────────────────────────────────────────────

const KNOWN_FLAGS = ['beta_invoices', 'dark_mode_v2', 'advanced_reporting', 'push_notifications']

function FlagsTab() {
  const [userSearch, setUserSearch] = useState('')
  const [selectedUserId, setSelectedUserId] = useState('')
  const [newFlag, setNewFlag] = useState('')
  const [customFlag, setCustomFlag] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)

  const { data: users } = useAdminUsers(userSearch ? { search: userSearch } : {})
  const allUsers = users?.users || []

  const { data: flags = [], isLoading } = useFeatureFlags(selectedUserId || undefined)
  const setFlag    = useSetFeatureFlag()
  const deleteFlag = useDeleteFeatureFlag()

  const handleToggle = async (flag) => {
    try {
      await setFlag.mutateAsync({
        userId:    flag.user_id,
        flagName:  flag.flag_name,
        isEnabled: !flag.is_enabled,
      })
    } catch {
      toast.error('Failed to update flag')
    }
  }

  const handleAdd = async () => {
    const name = customFlag.trim() || newFlag
    if (!name || !selectedUserId) return toast.error('Select a user and enter a flag name')
    try {
      await setFlag.mutateAsync({ userId: selectedUserId, flagName: name, isEnabled: true })
      toast.success('Flag set')
      setShowAddForm(false)
      setNewFlag('')
      setCustomFlag('')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to set flag')
    }
  }

  const handleDelete = async (flag) => {
    if (!window.confirm(`Remove flag "${flag.flag_name}" from ${flag.user_name}?`)) return
    try {
      await deleteFlag.mutateAsync(flag.id)
      toast.success('Flag removed')
    } catch {
      toast.error('Failed to remove flag')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            className={clsx(inputCls, 'pl-9')}
            placeholder="Filter by user…"
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
          />
        </div>
        <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
          className={clsx(inputCls, 'w-auto cursor-pointer flex-1 min-w-48')}>
          <option value="">All users</option>
          {allUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
        </select>
        <button onClick={() => setShowAddForm(p => !p)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
                     text-white text-sm font-medium rounded-lg transition-colors">
          <Plus size={15} />
          Add Flag
        </button>
      </div>

      {showAddForm && (
        <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl space-y-3">
          <p className="text-xs font-medium text-[var(--text-secondary)]">Add flag to user</p>
          <div className="flex flex-wrap gap-3">
            <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
              className={clsx(inputCls, 'flex-1 min-w-48 cursor-pointer')} required>
              <option value="">Select user…</option>
              {allUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
            <select value={newFlag} onChange={e => setNewFlag(e.target.value)}
              className={clsx(inputCls, 'flex-1 min-w-36 cursor-pointer')}>
              <option value="">Choose flag…</option>
              {KNOWN_FLAGS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <input className={clsx(inputCls, 'flex-1 min-w-36')} placeholder="Or enter custom flag name"
              value={customFlag} onChange={e => setCustomFlag(e.target.value)} />
            <button onClick={handleAdd} disabled={setFlag.isPending}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {setFlag.isPending ? 'Saving…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3 animate-pulse">
            {[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-[var(--bg-overlay)] rounded-lg" />)}
          </div>
        ) : flags.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--text-muted)]">
            {selectedUserId ? 'No flags for this user.' : 'No feature flags configured.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-[var(--text-muted)] text-xs">
                  <th className="text-left px-4 py-3 font-medium">User</th>
                  <th className="text-left px-4 py-3 font-medium">Flag</th>
                  <th className="text-left px-4 py-3 font-medium">Enabled</th>
                  <th className="text-left px-4 py-3 font-medium">Since</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {flags.map(flag => (
                  <tr key={flag.id} className="hover:bg-[var(--bg-overlay)]">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="text-[var(--text-primary)] text-sm">{flag.user_name}</p>
                      <p className="text-[var(--text-muted)] text-xs">{flag.user_email}</p>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[var(--text-secondary)]">
                      {flag.flag_name}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggle(flag)}
                        disabled={setFlag.isPending}
                        className={clsx(
                          'relative inline-flex h-5 w-9 rounded-full transition-colors focus:outline-none disabled:opacity-50',
                          flag.is_enabled ? 'bg-indigo-600' : 'bg-[var(--bg-overlay)]'
                        )}
                      >
                        <span className={clsx(
                          'inline-block h-4 w-4 rounded-full bg-white shadow transition-transform mt-0.5',
                          flag.is_enabled ? 'translate-x-4' : 'translate-x-0.5'
                        )} />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-[var(--text-muted)] text-xs whitespace-nowrap">
                      {fmtDate(flag.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleDelete(flag)}
                        className="text-xs text-[var(--text-muted)] hover:text-red-400 transition-colors">
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Test Email Panel ─────────────────────────────────────────────────────────

function TestEmailPanel() {
  const { user }   = useAuth()
  const testEmail  = useAdminTestEmail()
  const [to, setTo] = useState(user?.email || '')
  const [sent, setSent] = useState(false)

  const handle = async (e) => {
    e.preventDefault()
    if (!to.trim()) return
    setSent(false)
    try {
      await testEmail.mutateAsync(to.trim())
      setSent(true)
      toast.success('Test email sent')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send test email')
    }
  }

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="mt-0.5 w-8 h-8 rounded-lg bg-indigo-600/15 flex items-center justify-center shrink-0">
          <Mail size={15} className="text-indigo-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[var(--text-primary)]">Send Test Email</p>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">
            Verify Resend is configured correctly by sending a test message.
          </p>
        </div>
      </div>
      <form onSubmit={handle} className="flex gap-2">
        <input
          type="email"
          value={to}
          onChange={e => { setTo(e.target.value); setSent(false) }}
          placeholder="recipient@example.com"
          className={clsx(inputCls, 'flex-1')}
          required
        />
        <button
          type="submit"
          disabled={testEmail.isPending || !to.trim()}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                     text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
        >
          {testEmail.isPending ? 'Sending…' : 'Send Test'}
        </button>
      </form>
      {sent && (
        <p className="mt-2 text-xs text-green-400">
          Sent — check the email log below to confirm delivery status.
        </p>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('users')
  const current = TABS.find(t => t.id === activeTab)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Admin Panel</h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">Platform management — users, garages, audit logs, and feature flags.</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[var(--border)]">
        {TABS.map(tab => {
          const Icon = tab.icon
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-400'
                  : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              )}>
              <Icon size={15} />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'users'   && <UsersTab />}
      {activeTab === 'garages' && <GaragesTab />}
      {activeTab === 'audit'   && <AuditTab />}
      {activeTab === 'flags'   && <FlagsTab />}
      {activeTab === 'emails'  && (
        <div className="space-y-6">
          <TestEmailPanel />
          <EmailLogPage />
        </div>
      )}
    </div>
  )
}
